import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ban, ChevronDown, ChevronUp, Loader2, MapPin, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { confirmDialog } from "@/components/ui/app-dialogs";

/**
 * FIELD RELIABILITY — office bulk removal review.
 *
 * ST-imported ghost devices get flagged `device_gone` by techs faster
 * than the office can clear them one job at a time. This card lists every
 * asset awaiting removal review, grouped per site, with the requesting
 * tech, note, date, and how many open jobs each device still blocks.
 * Office selects rows (select-all per site) and retires them in one
 * confirmed batch — the server flips status to 'retired' (FK-safe,
 * history row, audited) and auto-releases open gating job links so stuck
 * jobs unblock. "Keep" clears a single flag via the existing per-asset
 * removal-review endpoint.
 */

interface PendingAsset {
    id: string;
    name: string | null;
    assetType: string | null;
    status: string | null;
    manufacturer: string | null;
    model: string | null;
    serialNumber: string | null;
    requestedAt: string | null;
    requestedById: string | null;
    requestedByName: string | null;
    note: string | null;
    blockedOpenJobs: number;
}

interface PendingSite {
    siteId: string;
    siteName: string | null;
    address: string | null;
    customerName: string | null;
    assets: PendingAsset[];
}

interface PendingResponse {
    sites: PendingSite[];
    totalAssets: number;
    totalBlockedJobs: number;
}

interface BulkRetireResponse {
    retired: Array<{ assetId: string; releasedJobIds: string[] }>;
    retiredCount: number;
    releasedJobLinks: number;
    skipped: Array<{ assetId: string; reason: string }>;
}

function deviceLabel(a: PendingAsset): string {
    const make = [a.manufacturer, a.model].filter(Boolean).join(" ");
    const name = a.name || a.assetType || "Device";
    return make ? `${name} — ${make}` : name;
}

export function RemovalReviewCard() {
    const qc = useQueryClient();
    const [expanded, setExpanded] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    const pending = useQuery({
        queryKey: ["removal-review", "pending"],
        queryFn: () => api.get<PendingResponse>("/api/removal-review/pending"),
        refetchInterval: 60_000,
    });

    const sites = useMemo(() => pending.data?.sites ?? [], [pending.data]);
    const allAssets = useMemo(() => sites.flatMap((s) => s.assets), [sites]);
    const selectedAssets = useMemo(() => allAssets.filter((a) => selected.has(a.id)), [allAssets, selected]);
    const selectedBlockedJobs = selectedAssets.reduce((n, a) => n + a.blockedOpenJobs, 0);

    const invalidate = () => {
        setSelected(new Set());
        qc.invalidateQueries({ queryKey: ["removal-review", "pending"] });
        // Released links change device gates on jobs sitting in the queue.
        qc.invalidateQueries({ queryKey: ["office-review"] });
    };

    const bulkRetire = useMutation({
        mutationFn: (assetIds: string[]) =>
            api.post<BulkRetireResponse>("/api/removal-review/bulk-retire", { assetIds }),
        onSuccess: (res) => {
            invalidate();
            const bits = [
                `Retired ${res.retiredCount} device${res.retiredCount === 1 ? "" : "s"}`,
                `released ${res.releasedJobLinks} blocked job link${res.releasedJobLinks === 1 ? "" : "s"}`,
            ];
            if (res.skipped.length > 0) bits.push(`skipped ${res.skipped.length}`);
            toast.success(bits.join(" — "), { duration: 8_000 });
        },
        onError: (e: any) => toast.error(`Bulk retire failed: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });

    const keepDevice = useMutation({
        mutationFn: (assetId: string) => api.post(`/api/assets/${assetId}/removal-review`, { action: "reject" }),
        onSuccess: () => {
            invalidate();
            toast.success("Kept — flag cleared, device stays active");
        },
        onError: (e: any) => toast.error(`Keep failed: ${e?.body?.message ?? "unknown"}`),
    });

    if (!pending.data || pending.data.totalAssets === 0) return null;
    const { totalAssets, totalBlockedJobs } = pending.data;

    const toggleAsset = (id: string, on: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const toggleSite = (site: PendingSite, on: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            for (const a of site.assets) {
                if (on) next.add(a.id);
                else next.delete(a.id);
            }
            return next;
        });
    };

    const onRetireSelected = async () => {
        const n = selectedAssets.length;
        if (n === 0) return;
        const confirmed = await confirmDialog({
            title: "Retire devices?",
            message:
                `Retire ${n} device${n === 1 ? "" : "s"} and release ${selectedBlockedJobs} blocked job${selectedBlockedJobs === 1 ? "" : "s"}?\n\n` +
                "Each device is marked retired (kept in history, hidden from active lists) and any open job " +
                "still waiting on it is released with disposition “not located — device gone”.",
            confirmLabel: `Retire ${n} device${n === 1 ? "" : "s"}`,
            destructive: true,
        });
        if (!confirmed) return;
        bulkRetire.mutate(selectedAssets.map((a) => a.id));
    };

    return (
        <div className="border-b border-red-300/60 bg-red-50 dark:border-red-500/40 dark:bg-red-950/30">
            <button
                type="button"
                data-testid="removal-review-strip"
                onClick={() => setExpanded((v) => !v)}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-800 hover:bg-red-100 dark:text-red-200 dark:hover:bg-red-950/50"
            >
                <Trash2 className="h-4 w-4 shrink-0" />
                <span>
                    <span className="font-semibold">{totalAssets}</span> device{totalAssets === 1 ? "" : "s"} flagged as
                    gone by techs
                    {totalBlockedJobs > 0 && (
                        <>
                            {" "}
                            — blocking <span className="font-semibold">{totalBlockedJobs}</span> open job
                            {totalBlockedJobs === 1 ? "" : "s"}
                        </>
                    )}
                    . Confirm removal or keep.
                </span>
                <span className="ml-auto inline-flex items-center gap-1 font-medium underline">
                    {expanded ? "Hide" : "Review"}
                    {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </span>
            </button>

            {expanded && (
                <div className="px-4 pb-3 space-y-3">
                    {sites.map((site) => {
                        const siteAllSelected = site.assets.every((a) => selected.has(a.id));
                        return (
                            <div
                                key={site.siteId}
                                data-testid="removal-review-site"
                                className="rounded-lg border border-red-200 bg-card dark:border-red-500/30"
                            >
                                <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
                                    <Checkbox
                                        checked={siteAllSelected}
                                        onCheckedChange={(v) => toggleSite(site, v === true)}
                                        aria-label={`Select all devices at ${site.siteName ?? "site"}`}
                                    />
                                    <MapPin className="h-3.5 w-3.5 text-foreground/60 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold truncate">
                                            {site.siteName || "Unnamed site"}
                                            {site.customerName ? (
                                                <span className="font-normal text-foreground/70">
                                                    {" "}
                                                    · {site.customerName}
                                                </span>
                                            ) : null}
                                        </p>
                                        {site.address && (
                                            <p className="text-[11px] text-foreground/60 truncate">{site.address}</p>
                                        )}
                                    </div>
                                    <span className="ml-auto text-xs text-foreground/70 shrink-0">
                                        {site.assets.length} device{site.assets.length === 1 ? "" : "s"}
                                    </span>
                                </div>
                                <ul className="divide-y divide-border/50">
                                    {site.assets.map((a) => (
                                        <li key={a.id} className="flex items-start gap-2 px-3 py-2">
                                            <Checkbox
                                                className="mt-0.5"
                                                checked={selected.has(a.id)}
                                                onCheckedChange={(v) => toggleAsset(a.id, v === true)}
                                                aria-label={`Select ${deviceLabel(a)}`}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium">
                                                    {deviceLabel(a)}
                                                    {a.serialNumber && (
                                                        <span className="text-foreground/70">
                                                            {" "}
                                                            · SN {a.serialNumber}
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-[11px] text-foreground/60">
                                                    Flagged {a.requestedByName ? `by ${a.requestedByName} ` : ""}
                                                    {a.requestedAt
                                                        ? `on ${new Date(a.requestedAt).toLocaleDateString()}`
                                                        : ""}
                                                </p>
                                                {a.note && (
                                                    <p className="text-xs text-foreground/80 mt-0.5">
                                                        &ldquo;{a.note}&rdquo;
                                                    </p>
                                                )}
                                            </div>
                                            <span
                                                className={cn(
                                                    "shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold",
                                                    a.blockedOpenJobs > 0
                                                        ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                                                        : "bg-secondary text-foreground/60",
                                                )}
                                            >
                                                {a.blockedOpenJobs > 0
                                                    ? `blocks ${a.blockedOpenJobs} job${a.blockedOpenJobs === 1 ? "" : "s"}`
                                                    : "no open jobs"}
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 shrink-0"
                                                disabled={keepDevice.isPending}
                                                onClick={() => keepDevice.mutate(a.id)}
                                                title="Clear the flag — the device exists and stays active"
                                            >
                                                <Ban className="h-3.5 w-3.5 mr-1" />
                                                Keep
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}

                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-foreground/70">
                            {selectedAssets.length} selected
                            {selectedAssets.length > 0 &&
                                ` · ${selectedBlockedJobs} blocked job${selectedBlockedJobs === 1 ? "" : "s"} to release`}
                        </span>
                        <Button
                            size="sm"
                            variant="destructive"
                            data-testid="retire-selected"
                            disabled={selectedAssets.length === 0 || bulkRetire.isPending}
                            onClick={() => void onRetireSelected()}
                        >
                            {bulkRetire.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                            )}
                            Retire selected{selectedAssets.length > 0 ? ` (${selectedAssets.length})` : ""}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
